import { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { keysIn, omit, includes, get } from "lodash"
import {
  TextField,
  FormControl,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  DialogTitle,
} from "@mui/material"
import { Search } from "@mui/icons-material"
import { validateTopicName, computeSecurityInstrumentName } from "helpers/topics"
import UnifiedSearch from "modules/unified-search"
import ModelSelectorAutocomplete from "connected-components/model-selector-autocomplete"

import "./style.scss"

function computeDisabledButton({ name, errors, instrument }) {
  return !instrument || !name || keysIn(errors).length > 0
}

function disableSearchResult(isSuperuser) {
  return (item) => !includes(isSuperuser ? ["security", "portfolio"] : ["security"], item.type) || item.assetClass === "swap"
}

class EditTopic extends Component {
  static propTypes = {
    close: PropTypes.func,
    applyAction: PropTypes.func,
    saveTopic: PropTypes.func.isRequired,
    models: PropTypes.object,
    topic: PropTypes.object.isRequired,
    account: PropTypes.string.isRequired,
    modelId: PropTypes.string,
    setModel: PropTypes.func,
    portfolioName: PropTypes.string,
    isSuperuser: PropTypes.bool.isRequired,
  }

  static defaultProps = {
    topic: {},
    models: {},
  }

  constructor(props) {
    super(props)
    const errors = {}
    let instrument = null
    const { securityId, portfolioId } = props.topic.referenceInstrument || {}
    if (portfolioId) {
      instrument = {
        ...props.topic.referenceInstrument,
        name: props.portfolioName || props.topic.portfolioName,
      }
    } else if (securityId) {
      instrument = {
        ...securityId,
        name: computeSecurityInstrumentName(securityId),
      }
    }

    const state = {
      id: props.topic.id || null,
      instrument,
      modelId: props.modelId,
      name: props.topic.name || "",
      description: props.topic.description || "",
    }

    if (state.name && !validateTopicName(state.name)) {
      errors.name = true
    }

    this.state = { ...state, errors, submitDisabled: true }
  }

  onChange = event => {
    const { value, name } = event.target
    const changes = {
      errors: { ...this.state.errors },
    }

    if (name !== "name") {
      changes[name] = value
    } else {
      changes.name = value

      if (!validateTopicName(value) && !changes.errors.name) {
        changes.errors.name = true
      } else if (validateTopicName(value) && changes.errors[name]) {
        changes.errors = omit(changes.errors, name)
      }
    }

    const newState = { ...this.state, ...changes }
    newState.submitDisabled = computeDisabledButton(newState)
    this.setState(newState)
  }

  submitTopic = () => {
    const { saveTopic, close } = this.props
    const { id, name, description, instrument } = this.state
    saveTopic(id, { name, instrument, description })
    close()
  }

  onKeyPress = event => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      const errors = this.validateTopic()
      if (keysIn(errors).length === 0) {
        this.saveTopic()
      }
    }
  }

  saveTopic = () => {
    const { applyAction } = this.props
    const errors = this.validateTopic()

    if (keysIn(errors).length > 0) {
      this.setState({ errors, submitDisabled: true })
    } else if (applyAction) {
      applyAction(this.submitTopic, null, null, true)()
    } else {
      this.submitTopic()
    }
  }

  validateTopic = () => {
    const { name } = this.state
    const errors = {}

    if (!validateTopicName(name)) {
      errors.name = true
    }

    return errors
  }

  showInstrumentSearch = () => {
    this.setState({ instrument: null })
  }

  searchItemsActions = () => ({
    security: {
      noDispatch: true,
      mainAction: item => () => {
        this.setState({ instrument: item, focus: true })
      },
    },
    portfolio: {
      noDispatch: true,
      mainAction: item => () => {
        this.setState({ instrument: item, focus: true })
      },
    },
  })

  handleModelChange = (modelId) => {
    const { setModel } = this.props
    this.setState({ modelId })
    setModel(modelId)
  }

  render() {
    const { close, account, models, isSuperuser } = this.props
    const { name, id, errors, submitDisabled, modelId, instrument, description, focus } = this.state
    const modelsList = Array.isArray(models) ? models : Object.values(models || {})
    const emptySearch = <div className="compare-search-empty">
      Search for a reference instrument
    </div>

    return <div className="components_edit-topic">
      <DialogTitle>
        <span>{id ? "Edit Research Topic" : "Create Research Topic For..."}</span>
        {!instrument
          && <ModelSelectorAutocomplete
            className="model-selector"
            models={modelsList}
            value={modelId || ""}
            onChange={this.handleModelChange}
          />}
      </DialogTitle>
      {instrument
        ? <>
          <DialogContent className="edit-topic-content-wrapper">
            <div>
              <FormControl variant="standard" className="topic-input-wrapper">
                <TextField
                  variant="standard"
                  disabled={!!id}
                  id="topic-instrument"
                  name="instrument"
                  label="Reference instrument"
                  value={instrument.name}
                  onFocus={this.showInstrumentSearch}
                  margin="normal"
                  slotProps={{
                      input: {
                        classes: { root: "topic-instrument" },
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      },
                  }}
                />
              </FormControl>
              <FormControl variant="standard" className="topic-input-wrapper">
                <TextField
                  variant="standard"
                  id="topic-name"
                  autoFocus={focus}
                  name="name"
                  label="Research topic name"
                  value={name}
                  error={errors.name}
                  onChange={this.onChange}
                  onKeyDown={this.onKeyPress}
                  margin="normal"
                  helperText={errors.name ? "Invalid name. Please follow naming guidelines." : "3-50 chars long. Alphanum, _, -, and spaces."}
                  slotProps={{
                      formHelperText: { error: errors.name, classes: { root: "topic-name-error" } },
                  }}
                />
              </FormControl>
              <FormControl variant="standard" className="topic-input-wrapper">
                <TextField
                  variant="standard"
                  id="topic-description"
                  name="description"
                  multiline
                  label="Research topic description"
                  value={description}
                  onChange={this.onChange}
                  onKeyDown={this.onKeyPress}
                  margin="normal"
                />
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <Button color="secondary" onClick={close}>Cancel</Button>
            <Button color="primary" disabled={submitDisabled} onClick={this.saveTopic}>
              {id ? "Update Topic" : "Create Topic"}
            </Button>
          </DialogActions>
        </>
        : <UnifiedSearch
            autoFocus
            skipClose
            account={account}
            modelId={modelId}
            itemsActions={this.searchItemsActions()}
            emptyContent={emptySearch}
            disableResult={disableSearchResult(isSuperuser)}
            hiddenTypes={["researchTopics"]}
        />}
    </div>
  }
}

export default connect(state => ({ isSuperuser: get(state, "user.isSuperuser", false) }))(EditTopic)
